import { useMemo, useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { conversationsUtilsEndpoints } from '@/api/endpoints';
import { callPost } from '@/api/fetcher';
import useMessages from '@/hooks/useMessages';
import { toErrorReason } from '@/segment/errorReason';
import { trackEvent } from '@/segment/segment';
import { ConversationEvent } from '@/shared/types/chat';
import { EventName } from '@/shared/types/segment';

import { endpoints as eventsEndpoints, useConversationEvents } from './useConversationEvents';

interface SummarizeParams {
  sessionId: string;
  deskId: string;
}

/**
 * Hook for summarizing conversations using AI
 *
 * This hook provides a mutation to generate AI summaries of conversations.
 *
 * @param deskId - The desk ID
 * @param sessionId - The session/conversation ID
 * @returns Mutation object with summarize function, loading state, error state, error object, and hasNewMessages flag
 */
export const useSummarize = (deskId: string, sessionId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { events } = useConversationEvents(deskId, sessionId);
  const { messages } = useMessages(deskId, sessionId);

  // Check if new messages exist since last summary
  const hasNewMessagesSinceLastSummary = useMemo(() => {
    const lastSummary = events?.reduce<(typeof events)[0] | undefined>((latest, e) => {
      if (e.type !== ConversationEvent.SUMMARY) {
        return latest;
      }
      if (!latest || new Date(e.created).getTime() > new Date(latest.created).getTime()) {
        return e;
      }
      return latest;
    }, undefined);

    const lastMessage = messages?.pages?.[0]?.messages?.[0];

    if (!lastSummary) {
      return true; // No summary exists, allow
    }
    if (!lastMessage) {
      return false; // No messages at all, don't allow
    }

    return new Date(lastMessage.created).getTime() > new Date(lastSummary.created).getTime();
  }, [events, messages]);

  const {
    mutate: summarizeMutation,
    isPending,
    isError,
    error,
  } = useMutation({
    mutationFn: async ({ sessionId }: SummarizeParams) => {
      return callPost(conversationsUtilsEndpoints.summarize(sessionId), {});
    },
    onSuccess: (_, { sessionId, deskId }) => {
      // Invalidate conversation events to refetch and show the new summary event
      queryClient.invalidateQueries({ queryKey: [eventsEndpoints.events(deskId, sessionId)] });
      toast.success(t('conversation.summary.success'));
      trackEvent(EventName.ConversationSummarizeSuccess, { session_id: sessionId });
    },
    onError: (error, { sessionId: submittedSessionId }) => {
      toast.error(t('conversation.summary.error'));
      trackEvent(EventName.ConversationSummarizeError, {
        session_id: submittedSessionId,
        error_reason: toErrorReason(error),
      });
    },
  });

  // Wrapper function that validates before calling mutation
  const summarize = useCallback(
    (params: SummarizeParams) => {
      if (!hasNewMessagesSinceLastSummary) {
        toast.warning(t('conversation.summary.no_new_messages'));
        trackEvent(EventName.ConversationSummarizeNoNewMessages, { session_id: params.sessionId });
        return;
      }
      summarizeMutation(params);
    },
    [hasNewMessagesSinceLastSummary, summarizeMutation, t]
  );

  return { summarize, isPending, isError, error, hasNewMessages: hasNewMessagesSinceLastSummary };
};
